import { useCallback, useEffect, useState } from "react";
import { RoleCodes } from "@tm-food/api-contract";

export type OpsDashboardTabId = "prod" | "qc" | "wh";

export type OpsDashboardWidgetId =
  // Production (7 widgets)
  | "bi.prod.kpis"
  | "bi.prod.output"
  | "bi.prod.loss"
  | "bi.prod.funnel"
  | "bi.prod.otif"
  | "bi.prod.wip"
  | "bi.prod.live"
  // Quality (7 widgets)
  | "bi.qc.kpis"
  | "bi.qc.trend"
  | "bi.qc.defect_donut"
  | "bi.qc.funnel"
  | "bi.qc.matrix"
  | "bi.qc.ccp"
  | "bi.qc.live"
  // Warehouse (7 widgets)
  | "bi.wh.kpis"
  | "bi.wh.flow_chart"
  | "bi.wh.stock_donut"
  | "bi.wh.funnel"
  | "bi.wh.expiry"
  | "bi.wh.zones"
  | "bi.wh.live";

export type OpsDashboardWidgetMeta = {
  id: OpsDashboardWidgetId;
  tabId: OpsDashboardTabId;
  code: OpsDashboardWidgetId;
  label: string;
  shortLabel: string;
  sectionLabel: string;
  description: string;
};

export type OpsDashboardTabMeta = {
  id: OpsDashboardTabId;
  label: string;
  fullTitle: string;
  badge: string;
  permissionCode: string;
  description: string;
};

export const OPS_DASHBOARD_TABS: readonly OpsDashboardTabMeta[] = [
  {
    id: "prod",
    label: "Sản xuất",
    fullTitle: "Báo cáo Sản xuất",
    badge: "Lệnh sản xuất",
    permissionCode: "bi.prod.list",
    description: "Theo dõi mẻ đang sản xuất trên chuyền, tiến độ lệnh và phân tích nguy cơ giao hàng.",
  },
  {
    id: "qc",
    label: "Chất lượng",
    fullTitle: "Báo cáo Chất lượng",
    badge: "Kiểm tra chất lượng",
    permissionCode: "bi.qc.list",
    description: "Theo dõi kiểm định đầu vào, thành phẩm, hồ sơ sự cố không phù hợp và tạm giữ kiểm tra.",
  },
  {
    id: "wh",
    label: "Kho",
    fullTitle: "Báo cáo Kho",
    badge: "Tồn kho & Hạn dùng",
    permissionCode: "bi.wh.list",
    description: "Lô hàng khả dụng, phân tích tình trạng tồn kho, cảnh báo cận hạn sử dụng và dưới ngưỡng an toàn.",
  },
] as const;

export const OPS_DASHBOARD_WIDGETS: readonly OpsDashboardWidgetMeta[] = [
  // 1. SẢN XUẤT (7 widgets)
  {
    id: "bi.prod.kpis",
    tabId: "prod",
    code: "bi.prod.kpis",
    label: "Chỉ số hiệu suất sản xuất trọng yếu (4 thẻ KPI)",
    shortLabel: "4 Thẻ KPI Sản xuất",
    sectionLabel: "Khối 1 · KPI Trọng yếu",
    description: "Tiến độ kế hoạch, mẻ đang sản xuất, tỷ lệ thu hồi và đơn bán rủi ro giao hàng.",
  },
  {
    id: "bi.prod.output",
    tabId: "prod",
    code: "bi.prod.output",
    label: "Biểu đồ sản lượng kế hoạch & thực tế",
    shortLabel: "Chart Sản lượng",
    sectionLabel: "Khối 2 · Sản lượng & Hao hụt",
    description: "Biểu đồ cột kép so sánh sản lượng mục tiêu và thực đạt theo ngày/dây chuyền.",
  },
  {
    id: "bi.prod.loss",
    tabId: "prod",
    code: "bi.prod.loss",
    label: "Biểu đồ cơ cấu hao hụt & tỷ lệ thu hồi",
    shortLabel: "Donut Hao hụt",
    sectionLabel: "Khối 2 · Sản lượng & Hao hụt",
    description: "Biểu đồ tròn phân rã nguyên nhân hao hụt chế biến và số mẻ vượt định mức.",
  },
  {
    id: "bi.prod.funnel",
    tabId: "prod",
    code: "bi.prod.funnel",
    label: "Phễu tiến độ vận hành lệnh sản xuất (MO)",
    shortLabel: "Phễu Lệnh MO",
    sectionLabel: "Khối 3 · Tiến độ MO & Giao hàng",
    description: "Tỷ lệ chuyển đổi lệnh sản xuất từ Đã phát hành -> Đang chạy -> Hoàn tất.",
  },
  {
    id: "bi.prod.otif",
    tabId: "prod",
    code: "bi.prod.otif",
    label: "Cảnh báo rủi ro tiến độ giao hàng đơn bán (OTIF)",
    shortLabel: "Rủi ro Giao hàng",
    sectionLabel: "Khối 3 · Tiến độ MO & Giao hàng",
    description: "Danh sách đơn hàng đối tác khẩn cấp (≤ 3 ngày) và cần theo dõi (4-7 ngày).",
  },
  {
    id: "bi.prod.wip",
    tabId: "prod",
    code: "bi.prod.wip",
    label: "Tải công suất trạm máy & điểm nghẽn bán thành phẩm",
    shortLabel: "Radar Trạm máy",
    sectionLabel: "Khối 4 · Công suất Trạm máy",
    description: "Giám sát mẻ đang gia công tại các công đoạn Sơ chế, Phối trộn, Thanh trùng, Đóng gói.",
  },
  {
    id: "bi.prod.live",
    tabId: "prod",
    code: "bi.prod.live",
    label: "Bảng điều hành phân xưởng & mẻ đang vận hành",
    shortLabel: "Bảng Mẻ trực tiếp",
    sectionLabel: "Khối 5 · Điều hành trực tiếp",
    description: "Bảng theo dõi tiến trình công đoạn, trạng thái và nhật ký mẻ trực tiếp trên chuyền.",
  },

  // 2. CHẤT LƯỢNG (7 widgets)
  {
    id: "bi.qc.kpis",
    tabId: "qc",
    code: "bi.qc.kpis",
    label: "Chỉ số kiểm soát chất lượng trọng yếu (4 thẻ KPI)",
    shortLabel: "4 Thẻ KPI Chất lượng",
    sectionLabel: "Khối 1 · KPI Trọng yếu",
    description: "Tỷ lệ đạt chuẩn lần đầu, hàng chờ kiểm định, hồ sơ sự cố NCR và lô tạm giữ.",
  },
  {
    id: "bi.qc.trend",
    tabId: "qc",
    code: "bi.qc.trend",
    label: "Biểu đồ xu hướng kiểm định & tỷ lệ đạt chuẩn",
    shortLabel: "Chart Xu hướng QC",
    sectionLabel: "Khối 2 · Xu hướng & Cơ cấu lỗi",
    description: "Biểu đồ theo dõi tỷ lệ lô đạt chuẩn qua các kỳ kiểm nghiệm IQC/IPQC/FQC.",
  },
  {
    id: "bi.qc.defect_donut",
    tabId: "qc",
    code: "bi.qc.defect_donut",
    label: "Biểu đồ cơ cấu nguyên nhân sự cố & lỗi",
    shortLabel: "Donut Nguyên nhân lỗi",
    sectionLabel: "Khối 2 · Xu hướng & Cơ cấu lỗi",
    description: "Phân tích tỷ trọng lỗi cảm quan, hóa lý, vi sinh và quy cách bao bì.",
  },
  {
    id: "bi.qc.funnel",
    tabId: "qc",
    code: "bi.qc.funnel",
    label: "Phễu 4 cổng kiểm soát chất lượng (IQC - IPQC - FQC - QA)",
    shortLabel: "Phễu 4 Cổng QC",
    sectionLabel: "Khối 3 · Cổng QC & Ma trận rủi ro",
    description: "Tỷ lệ thông qua và số lượng hồ sơ chờ xử lý tại 4 cổng kiểm soát chất lượng.",
  },
  {
    id: "bi.qc.matrix",
    tabId: "qc",
    code: "bi.qc.matrix",
    label: "Ma trận mức độ nghiêm trọng sự cố & lô tạm giữ",
    shortLabel: "Ma trận Sự cố NCR",
    sectionLabel: "Khối 3 · Cổng QC & Ma trận rủi ro",
    description: "Phân loại sự cố Nghiêm trọng / Lớn / Nhỏ và danh sách hồ sơ NCR cần thẩm định.",
  },
  {
    id: "bi.qc.ccp",
    tabId: "qc",
    code: "bi.qc.ccp",
    label: "Giám sát điểm kiểm soát tới hạn (HACCP / CCP)",
    shortLabel: "Radar Điểm CCP",
    sectionLabel: "Khối 4 · Giám sát HACCP",
    description: "Thông số cảm biến nhiệt độ thanh trùng UHT, máy dò kim loại, độ kín mí lon và pH.",
  },
  {
    id: "bi.qc.live",
    tabId: "qc",
    code: "bi.qc.live",
    label: "Bảng điều hành kiểm tra & hồ sơ kiểm nghiệm trực tiếp",
    shortLabel: "Bảng Kiểm nghiệm",
    sectionLabel: "Khối 5 · Điều hành trực tiếp",
    description: "Danh sách mẫu thử nghiệm trực tiếp và trạng thái phê duyệt xuất xưởng.",
  },

  // 3. KHO VẬN (7 widgets)
  {
    id: "bi.wh.kpis",
    tabId: "wh",
    code: "bi.wh.kpis",
    label: "Chỉ số trữ lượng & an toàn kho trọng yếu (4 thẻ KPI)",
    shortLabel: "4 Thẻ KPI Kho vận",
    sectionLabel: "Khối 1 · KPI Trọng yếu",
    description: "Lô khả dụng xuất kho, cảnh báo cận hạn sử dụng, dưới tồn an toàn và lô tạm giữ/biệt trữ.",
  },
  {
    id: "bi.wh.flow_chart",
    tabId: "wh",
    code: "bi.wh.flow_chart",
    label: "Biểu đồ biến động lưu lượng nhập - xuất kho",
    shortLabel: "Chart Nhập - Xuất",
    sectionLabel: "Khối 2 · Lưu lượng & Cơ cấu tồn",
    description: "Cân đối khối lượng hàng nhập kho và xuất cấp theo ngày hoặc theo phân khu.",
  },
  {
    id: "bi.wh.stock_donut",
    tabId: "wh",
    code: "bi.wh.stock_donut",
    label: "Biểu đồ cơ cấu trữ lượng & phân loại tồn kho",
    shortLabel: "Donut Cơ cấu tồn",
    sectionLabel: "Khối 2 · Lưu lượng & Cơ cấu tồn",
    description: "Phân bổ tỷ trọng lô hàng khả dụng, tạm giữ, cách ly và cận hạn.",
  },
  {
    id: "bi.wh.funnel",
    tabId: "wh",
    code: "bi.wh.funnel",
    label: "Phễu lưu chuyển kho 4 chặng (Nhập -> Lưu -> Soạn -> Giao)",
    shortLabel: "Phễu 4 Chặng Kho",
    sectionLabel: "Khối 3 · Luân chuyển & Hạn dùng",
    description: "Hiệu suất xử lý qua 4 chặng tiếp nhận, định vị kệ, cấp phát xưởng và đóng gói giao.",
  },
  {
    id: "bi.wh.expiry",
    tabId: "wh",
    code: "bi.wh.expiry",
    label: "Ma trận cảnh báo hạn sử dụng (FEFO) & tồn an toàn",
    shortLabel: "Ma trận Hạn dùng",
    sectionLabel: "Khối 3 · Luân chuyển & Hạn dùng",
    description: "Cảnh báo lô hạn dùng ≤ 7 ngày, 8-30 ngày và các mã vật tư chạm ngưỡng tồn tối thiểu.",
  },
  {
    id: "bi.wh.zones",
    tabId: "wh",
    code: "bi.wh.zones",
    label: "Tải trữ lượng & sức chứa 4 phân khu kho",
    shortLabel: "Radar 4 Phân khu",
    sectionLabel: "Khối 4 · Sức chứa Phân khu",
    description: "Mức chiếm dụng kệ và điều kiện nhiệt độ tại Kho Nguyên liệu, Kho Lạnh, Bao bì, Thành phẩm.",
  },
  {
    id: "bi.wh.live",
    tabId: "wh",
    code: "bi.wh.live",
    label: "Bảng điều hành thẻ kho & lô hàng trực tiếp",
    shortLabel: "Bảng Thẻ kho trực tiếp",
    sectionLabel: "Khối 5 · Điều hành trực tiếp",
    description: "Tra cứu trực tiếp vị trí kệ, tồn khả dụng và số ngày còn hạn của từng lô.",
  },
] as const;

export const PROD_WIDGET_IDS: OpsDashboardWidgetId[] = OPS_DASHBOARD_WIDGETS.filter(
  (w) => w.tabId === "prod",
).map((w) => w.id);

export const QC_WIDGET_IDS: OpsDashboardWidgetId[] = OPS_DASHBOARD_WIDGETS.filter(
  (w) => w.tabId === "qc",
).map((w) => w.id);

export const WH_WIDGET_IDS: OpsDashboardWidgetId[] = OPS_DASHBOARD_WIDGETS.filter(
  (w) => w.tabId === "wh",
).map((w) => w.id);

export const ALL_WIDGET_IDS: OpsDashboardWidgetId[] = OPS_DASHBOARD_WIDGETS.map((w) => w.id);

export function getWidgetsByTab(tabId: OpsDashboardTabId): OpsDashboardWidgetMeta[] {
  return OPS_DASHBOARD_WIDGETS.filter((w) => w.tabId === tabId);
}

export type RoleTabMap = Record<string, OpsDashboardTabId[]>;
export type RoleWidgetMap = Record<string, OpsDashboardWidgetId[]>;

/** Cấu hình 3-tick mặc định theo từng vai trò nghiệp vụ. */
export const DEFAULT_ROLE_TAB_PERMISSIONS: RoleTabMap = {
  [RoleCodes.Admin]: ["prod", "qc", "wh"],
  [RoleCodes.Bgd]: ["prod", "qc", "wh"],
  [RoleCodes.QuanDoc]: ["prod", "qc", "wh"],
  [RoleCodes.Qc]: ["qc"],
  [RoleCodes.Qa]: ["qc", "prod"],
  [RoleCodes.Kho]: ["wh"],
  [RoleCodes.NvSx]: ["prod"],
  [RoleCodes.KeHoach]: ["prod", "wh"],
  [RoleCodes.KinhDoanh]: ["prod", "wh"],
  [RoleCodes.MuaHang]: ["wh"],
};

/** Cấu hình quyền từng widget mặc định theo từng vai trò nghiệp vụ. */
export const DEFAULT_ROLE_WIDGET_PERMISSIONS: RoleWidgetMap = {
  [RoleCodes.Admin]: [...ALL_WIDGET_IDS],
  [RoleCodes.Bgd]: [...ALL_WIDGET_IDS],
  [RoleCodes.QuanDoc]: [...ALL_WIDGET_IDS],
  [RoleCodes.Qc]: [...QC_WIDGET_IDS],
  [RoleCodes.Qa]: [
    ...QC_WIDGET_IDS,
    "bi.prod.kpis",
    "bi.prod.loss",
    "bi.prod.funnel",
    "bi.prod.wip",
    "bi.prod.live",
  ],
  [RoleCodes.Kho]: [...WH_WIDGET_IDS],
  [RoleCodes.NvSx]: [
    "bi.prod.kpis",
    "bi.prod.output",
    "bi.prod.loss",
    "bi.prod.funnel",
    "bi.prod.wip",
    "bi.prod.live",
  ],
  [RoleCodes.KeHoach]: [
    "bi.prod.kpis",
    "bi.prod.output",
    "bi.prod.funnel",
    "bi.prod.otif",
    "bi.prod.wip",
    "bi.wh.kpis",
    "bi.wh.flow_chart",
    "bi.wh.stock_donut",
    "bi.wh.expiry",
    "bi.wh.zones",
  ],
  [RoleCodes.KinhDoanh]: [
    "bi.prod.kpis",
    "bi.prod.output",
    "bi.prod.funnel",
    "bi.prod.otif",
    "bi.wh.kpis",
    "bi.wh.stock_donut",
    "bi.wh.funnel",
    "bi.wh.expiry",
  ],
  [RoleCodes.MuaHang]: [
    "bi.wh.kpis",
    "bi.wh.flow_chart",
    "bi.wh.stock_donut",
    "bi.wh.expiry",
    "bi.wh.live",
  ],
};

export const KNOWN_ROLES_INFO: Array<{ code: string; name: string; department: string }> = [
  { code: RoleCodes.Admin, name: "Quản trị viên hệ thống", department: "IT / Ban điều hành" },
  { code: RoleCodes.Bgd, name: "Ban giám đốc", department: "Ban giám đốc" },
  { code: RoleCodes.QuanDoc, name: "Quản đốc xưởng", department: "Phân xưởng sản xuất" },
  { code: RoleCodes.Qc, name: "Nhân viên kiểm tra chất lượng", department: "Kiểm tra chất lượng" },
  { code: RoleCodes.Qa, name: "Chuyên viên đảm bảo chất lượng", department: "Đảm bảo chất lượng" },
  { code: RoleCodes.Kho, name: "Thủ kho", department: "Kho vận" },
  { code: RoleCodes.NvSx, name: "Nhân viên vận hành sản xuất", department: "Tổ đội chuyền sản xuất" },
  { code: RoleCodes.KeHoach, name: "Chuyên viên Kế hoạch", department: "Kế hoạch điều phối" },
  { code: RoleCodes.KinhDoanh, name: "Bộ phận Kinh doanh", department: "Bán hàng & chăm sóc khách hàng" },
  { code: RoleCodes.MuaHang, name: "Bộ phận Mua hàng", department: "Thu mua nguyên vật liệu" },
];

const STORAGE_KEY = "tmfood:ops_dashboard:role_tab_access:v2";
const WIDGET_STORAGE_KEY = "tmfood:ops_dashboard:role_widget_access:v1";
const EVENT_KEY = "tmfood:ops_dashboard:role_tab_access_changed";

/** Suy ra danh sách tab từ danh sách widget của 1 role */
export function deriveTabsFromWidgets(widgets: readonly OpsDashboardWidgetId[]): OpsDashboardTabId[] {
  const widgetSet = new Set(widgets);
  const tabs: OpsDashboardTabId[] = [];
  if (PROD_WIDGET_IDS.some((id) => widgetSet.has(id))) tabs.push("prod");
  if (QC_WIDGET_IDS.some((id) => widgetSet.has(id))) tabs.push("qc");
  if (WH_WIDGET_IDS.some((id) => widgetSet.has(id))) tabs.push("wh");
  return tabs;
}

/** Lấy cấu hình phân quyền tab từ localStorage hoặc fallback về mặc định */
export function getStoredRoleTabPermissions(): RoleTabMap {
  if (typeof window === "undefined") return { ...DEFAULT_ROLE_TAB_PERMISSIONS };
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULT_ROLE_TAB_PERMISSIONS };
    const parsed = JSON.parse(raw);
    if (typeof parsed === "object" && parsed !== null) {
      return { ...DEFAULT_ROLE_TAB_PERMISSIONS, ...parsed };
    }
  } catch {
    // Ignored fallback
  }
  return { ...DEFAULT_ROLE_TAB_PERMISSIONS };
}

/** Lấy cấu hình phân quyền từng widget từ localStorage hoặc fallback về mặc định */
export function getStoredRoleWidgetPermissions(): RoleWidgetMap {
  if (typeof window === "undefined") return { ...DEFAULT_ROLE_WIDGET_PERMISSIONS };
  try {
    const raw = window.localStorage.getItem(WIDGET_STORAGE_KEY);
    if (!raw) return { ...DEFAULT_ROLE_WIDGET_PERMISSIONS };
    const parsed = JSON.parse(raw);
    if (typeof parsed === "object" && parsed !== null) {
      return { ...DEFAULT_ROLE_WIDGET_PERMISSIONS, ...parsed };
    }
  } catch {
    // Ignored fallback
  }
  return { ...DEFAULT_ROLE_WIDGET_PERMISSIONS };
}

/** Lưu cấu hình phân quyền tab vào localStorage và phát sự kiện đồng bộ */
export function saveStoredRoleTabPermissions(mapping: RoleTabMap): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(mapping));
    window.dispatchEvent(new CustomEvent(EVENT_KEY, { detail: { tabMap: mapping } }));
  } catch {
    // Ignored storage quota or disabled storage
  }
}

/** Lưu cấu hình phân quyền widget vào localStorage, tự động đồng bộ tabMap và phát sự kiện */
export function saveStoredRoleWidgetPermissions(widgetMapping: RoleWidgetMap): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(WIDGET_STORAGE_KEY, JSON.stringify(widgetMapping));
    const currentTabs = getStoredRoleTabPermissions();
    const nextTabMap: RoleTabMap = { ...currentTabs };
    for (const [roleCode, widgets] of Object.entries(widgetMapping)) {
      nextTabMap[roleCode] = deriveTabsFromWidgets(widgets ?? []);
    }
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(nextTabMap));
    window.dispatchEvent(
      new CustomEvent(EVENT_KEY, {
        detail: { tabMap: nextTabMap, widgetMap: widgetMapping },
      }),
    );
  } catch {
    // Ignored storage quota
  }
}

/**
 * Xác định danh sách tab mà một tập hợp vai trò hoặc quyền hạn được phép xem.
 */
export function resolveVisibleDashboardTabs(options: {
  roleCodes: readonly string[];
  permissions?: readonly string[];
  mapping?: RoleTabMap;
  isSuperAdmin?: boolean;
}): OpsDashboardTabId[] {
  const { roleCodes, permissions = [], mapping = getStoredRoleTabPermissions(), isSuperAdmin = false } = options;

  const allowedSet = new Set<OpsDashboardTabId>();
  let hasRoleMapping = false;

  // 1. Kiểm tra cấu hình cụ thể theo từng vai trò trong mapping
  for (const roleCode of roleCodes) {
    if (roleCode in mapping) {
      hasRoleMapping = true;
      const tabsForRole = mapping[roleCode];
      if (Array.isArray(tabsForRole)) {
        for (const tabId of tabsForRole) {
          allowedSet.add(tabId);
        }
      }
    }
  }

  // 2. Dự phòng trực tiếp theo mã quyền granular nếu role mapping chưa có
  if (!hasRoleMapping) {
    const normPerms = new Set(permissions.map((p) => p.trim().toLowerCase().replace(/:/g, ".")));
    if (
      normPerms.has("bi.prod.list") ||
      normPerms.has("sys.dashboard.prod.list") ||
      PROD_WIDGET_IDS.some((w) => normPerms.has(w))
    ) {
      allowedSet.add("prod");
    }
    if (
      normPerms.has("bi.qc.list") ||
      normPerms.has("sys.dashboard.qc.list") ||
      QC_WIDGET_IDS.some((w) => normPerms.has(w))
    ) {
      allowedSet.add("qc");
    }
    if (
      normPerms.has("bi.wh.list") ||
      normPerms.has("sys.dashboard.wh.list") ||
      WH_WIDGET_IDS.some((w) => normPerms.has(w))
    ) {
      allowedSet.add("wh");
    }
  }

  // 3. Fallback cho Admin / SuperAdmin nếu chưa từng có cấu hình trong mapping
  if (allowedSet.size === 0 && (isSuperAdmin || roleCodes.includes(RoleCodes.Admin)) && !hasRoleMapping) {
    const adminTabs = mapping[RoleCodes.Admin] ?? ["prod", "qc", "wh"];
    for (const t of adminTabs) allowedSet.add(t);
  }

  // Giữ đúng thứ tự hiển thị chuẩn: prod -> qc -> wh
  return OPS_DASHBOARD_TABS.map((t) => t.id).filter((id) => allowedSet.has(id));
}

/**
 * Xác định danh sách các Widget được phép hiển thị cho người dùng hiện tại.
 */
export function resolveVisibleDashboardWidgets(options: {
  roleCodes: readonly string[];
  permissions?: readonly string[];
  widgetMapping?: RoleWidgetMap;
  tabId?: OpsDashboardTabId;
  isSuperAdmin?: boolean;
}): Set<OpsDashboardWidgetId> {
  const {
    roleCodes,
    permissions = [],
    widgetMapping = getStoredRoleWidgetPermissions(),
    tabId,
    isSuperAdmin = false,
  } = options;

  const allowed = new Set<OpsDashboardWidgetId>();
  let hasRoleMapping = false;

  for (const roleCode of roleCodes) {
    if (roleCode in widgetMapping) {
      hasRoleMapping = true;
      const list = widgetMapping[roleCode];
      if (Array.isArray(list)) {
        for (const wid of list) {
          allowed.add(wid);
        }
      }
    }
  }

  if (!hasRoleMapping) {
    const normPerms = new Set(permissions.map((p) => p.trim().toLowerCase().replace(/:/g, ".")));
    // Nếu có mã quyền từng widget cụ thể
    for (const w of OPS_DASHBOARD_WIDGETS) {
      if (normPerms.has(w.code)) {
        allowed.add(w.id);
      }
    }
    // Nếu chỉ có quyền cấp tab (chưa tách widget), cấp toàn bộ widget của tab đó
    if (normPerms.has("bi.prod.list") && !PROD_WIDGET_IDS.some((id) => allowed.has(id))) {
      for (const id of PROD_WIDGET_IDS) allowed.add(id);
    }
    if (normPerms.has("bi.qc.list") && !QC_WIDGET_IDS.some((id) => allowed.has(id))) {
      for (const id of QC_WIDGET_IDS) allowed.add(id);
    }
    if (normPerms.has("bi.wh.list") && !WH_WIDGET_IDS.some((id) => allowed.has(id))) {
      for (const id of WH_WIDGET_IDS) allowed.add(id);
    }
  }

  if (allowed.size === 0 && (isSuperAdmin || roleCodes.includes(RoleCodes.Admin)) && !hasRoleMapping) {
    for (const id of ALL_WIDGET_IDS) allowed.add(id);
  }

  if (tabId) {
    const filtered = new Set<OpsDashboardWidgetId>();
    for (const w of OPS_DASHBOARD_WIDGETS) {
      if (w.tabId === tabId && allowed.has(w.id)) {
        filtered.add(w.id);
      }
    }
    return filtered;
  }

  return allowed;
}

/**
 * Hook quản lý trạng thái phân quyền theo cả cấp Tab và cấp từng Widget cho mọi vai trò.
 */
export function useOpsDashboardRoleAccess() {
  const [roleTabMap, setRoleTabMap] = useState<RoleTabMap>(getStoredRoleTabPermissions);
  const [roleWidgetMap, setRoleWidgetMap] = useState<RoleWidgetMap>(getStoredRoleWidgetPermissions);

  useEffect(() => {
    const handleSync = () => {
      setRoleTabMap(getStoredRoleTabPermissions());
      setRoleWidgetMap(getStoredRoleWidgetPermissions());
    };

    window.addEventListener(EVENT_KEY, handleSync);
    window.addEventListener("storage", handleSync);
    return () => {
      window.removeEventListener(EVENT_KEY, handleSync);
      window.removeEventListener("storage", handleSync);
    };
  }, []);

  const updateRoleWidget = useCallback(
    (roleCode: string, widgetId: OpsDashboardWidgetId, checked: boolean) => {
      setRoleWidgetMap((prev) => {
        const current = prev[roleCode]
          ? [...prev[roleCode]]
          : [...(DEFAULT_ROLE_WIDGET_PERMISSIONS[roleCode] ?? [])];
        const nextWidgets = checked
          ? (Array.from(new Set([...current, widgetId])) as OpsDashboardWidgetId[])
          : current.filter((id) => id !== widgetId);

        const updatedWidgetMap: RoleWidgetMap = {
          ...prev,
          [roleCode]: nextWidgets,
        };
        saveStoredRoleWidgetPermissions(updatedWidgetMap);
        setRoleTabMap(getStoredRoleTabPermissions());
        return updatedWidgetMap;
      });
    },
    [],
  );

  const updateRoleTab = useCallback(
    (roleCode: string, tabId: OpsDashboardTabId, checked: boolean) => {
      const tabWidgets = getWidgetsByTab(tabId).map((w) => w.id);
      setRoleWidgetMap((prev) => {
        const current = prev[roleCode]
          ? [...prev[roleCode]]
          : [...(DEFAULT_ROLE_WIDGET_PERMISSIONS[roleCode] ?? [])];
        const nextWidgets = checked
          ? (Array.from(new Set([...current, ...tabWidgets])) as OpsDashboardWidgetId[])
          : current.filter((id) => !tabWidgets.includes(id));

        const updatedWidgetMap: RoleWidgetMap = {
          ...prev,
          [roleCode]: nextWidgets,
        };
        saveStoredRoleWidgetPermissions(updatedWidgetMap);
        setRoleTabMap(getStoredRoleTabPermissions());
        return updatedWidgetMap;
      });
    },
    [],
  );

  const setAllTabsForRole = useCallback((roleCode: string, enableAll: boolean) => {
    setRoleWidgetMap((prev) => {
      const nextWidgets: OpsDashboardWidgetId[] = enableAll ? [...ALL_WIDGET_IDS] : [];
      const updatedWidgetMap: RoleWidgetMap = {
        ...prev,
        [roleCode]: nextWidgets,
      };
      saveStoredRoleWidgetPermissions(updatedWidgetMap);
      setRoleTabMap(getStoredRoleTabPermissions());
      return updatedWidgetMap;
    });
  }, []);

  const setRoleWidgetsForRole = useCallback(
    (roleCode: string, widgets: OpsDashboardWidgetId[]) => {
      setRoleWidgetMap((prev) => {
        const updatedWidgetMap: RoleWidgetMap = {
          ...prev,
          [roleCode]: [...widgets],
        };
        saveStoredRoleWidgetPermissions(updatedWidgetMap);
        setRoleTabMap(getStoredRoleTabPermissions());
        return updatedWidgetMap;
      });
    },
    [],
  );

  const resetToDefaults = useCallback(() => {
    const resetTabs = { ...DEFAULT_ROLE_TAB_PERMISSIONS };
    const resetWidgets = { ...DEFAULT_ROLE_WIDGET_PERMISSIONS };
    saveStoredRoleWidgetPermissions(resetWidgets);
    saveStoredRoleTabPermissions(resetTabs);
    setRoleWidgetMap(resetWidgets);
    setRoleTabMap(resetTabs);
  }, []);

  return {
    roleTabMap,
    roleWidgetMap,
    updateRoleTab,
    updateRoleWidget,
    setAllTabsForRole,
    setRoleWidgetsForRole,
    resetToDefaults,
  };
}
